import { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getAllModalContentForSong,
  saveModalContent,
} from '../lib/supabase/modalContent';
import {
  fetchModalBlocks,
  fetchPathwayCards,
  saveModalBlock,
  type ModalBlock,
} from '../lib/actionModalProductionService';
import {
  seedActionModalsForLevel,
} from '../lib/actionModalSeedingService';
import {
  modalBlocksToLookDeeper,
  lookDeeperToModalBlocks,
  modalBlocksToOneKeyInsight,
  oneKeyInsightToModalBlocks,
  modalBlocksToTakeItFurther,
  takeItFurtherToModalBlocks,
  type LookDeeperContent,
  type OneKeyInsightContent,
  type TakeItFurtherContent,
} from '../lib/actionModalConverters';
import LookDeeperEditorEnhanced from './LookDeeperEditorEnhanced';
import ExplorerLookDeeperEditor, { type ExplorerLookDeeperEditorHandle } from './ExplorerLookDeeperEditor';
import OneKeyInsightEditorEnhanced from './OneKeyInsightEditorEnhanced';
import LensCardsEditor, { type LensCardsEditorHandle } from './LensCardsEditor';
import ExploreConnectionsPreview from './ExploreConnectionsPreview';
import ExplorerTimelineEditor, { type ExplorerTimelineEditorHandle } from './ExplorerTimelineEditor';
import TakeItFurtherEditorEnhanced from './TakeItFurtherEditorEnhanced';
import { supabase } from '../lib/supabaseClient';

interface ActionModalsEditorNewProps {
  songId: string | null;
  onContentChange?: (hasUnsavedChanges: boolean) => void;
}

export interface ActionModalsEditorHandle {
  save: () => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

const CIVIC_LEVELS = [
  { key: 'observer', label: 'Observer' },
  { key: 'explorer', label: 'Explorer' },
  { key: 'sense-maker', label: 'Sense-Maker' },
  { key: 'advocate', label: 'Advocate' },
  { key: 'catalyst', label: 'Catalyst' },
  { key: 'change-maker', label: 'Change-Maker' },
];

const MODAL_TABS = [
  { key: 'look_deeper', label: 'Look Deeper' },
  { key: 'one_key_insight', label: 'One Key Insight' },
  { key: 'explore_connections', label: 'Explore Connections' },
  { key: 'take_it_further', label: 'Take It Further' },
] as const;

type ModalTab = typeof MODAL_TABS[number]['key'];

const ActionModalsEditorNew = forwardRef<ActionModalsEditorHandle, ActionModalsEditorNewProps>(
  ({ songId, onContentChange }, ref) => {
    const [selectedLevel, setSelectedLevel] = useState('observer');
    const [activeModal, setActiveModal] = useState<ModalTab>('look_deeper');
    const [expandedSection, setExpandedSection] = useState<ModalTab | null>('look_deeper');
    const [loading, setLoading] = useState(false);

    // Structured content for each modal type
    const [lookDeeperContent, setLookDeeperContent] = useState<LookDeeperContent>({
      narrativeSections: [],
      timelineEvents: [],
    });
    const [oneKeyInsightContent, setOneKeyInsightContent] = useState<OneKeyInsightContent>({
      insight: '',
    });
    const [exploreConnectionsSongIds, setExploreConnectionsSongIds] = useState<string[]>([]);
    const [exploreConnectionsBlockId, setExploreConnectionsBlockId] = useState<string | null>(null);
    const [takeItFurtherContent, setTakeItFurtherContent] = useState<TakeItFurtherContent>({
      actionType: 'collect_only',
    });

    // Original content for dirty checking
    const [originalContent, setOriginalContent] = useState<string>('');
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [lensCardsChanged, setLensCardsChanged] = useState(false);
    const [explorerLookDeeperChanged, setExplorerLookDeeperChanged] = useState(false);
    const [explorerTimelineChanged, setExplorerTimelineChanged] = useState(false);
    const [observerQuizChanged, setObserverQuizChanged] = useState(false);

    // Refs for editors
    const lensCardsEditorRef = useRef<LensCardsEditorHandle>(null);
    const explorerLookDeeperEditorRef = useRef<ExplorerLookDeeperEditorHandle>(null);
    const explorerTimelineEditorRef = useRef<ExplorerTimelineEditorHandle>(null);
    const observerQuizEditorRef = useRef<ObserverOneKeyInsightQuizEditorHandle>(null);

    // Auto-expand most relevant section for civic level on mobile
    useEffect(() => {
      if (selectedLevel === 'observer') {
        setExpandedSection('look_deeper');
      } else if (selectedLevel === 'explorer' || selectedLevel === 'sense-maker') {
        setExpandedSection('one_key_insight');
      } else {
        setExpandedSection('take_it_further');
      }
    }, [selectedLevel]);

    useEffect(() => {
      // Reset flags before loading new level
      setInitialLoadComplete(false);
      setLensCardsChanged(false);
      setExplorerLookDeeperChanged(false);

      if (songId) {
        loadModalContent();
      } else {
        resetToEmpty();
      }
    }, [songId, selectedLevel]);

    function resetToEmpty() {
      const emptyLookDeeperContent = {
        narrativeSections: [
          { id: 'behind-the-song', title: 'Behind the Song', body: '', locked: true },
          { id: 'what-its-really-about', title: "What It's Really About", body: '', locked: true },
        ],
        timelineEvents: [],
      };
      const emptyOneKeyInsightContent = { insight: '' };
      const emptyExploreConnectionsSongIds: string[] = [];
      const emptyTakeItFurtherContent = { actionType: 'collect_only' as const };

      setLookDeeperContent(emptyLookDeeperContent);
      setOneKeyInsightContent(emptyOneKeyInsightContent);
      setExploreConnectionsSongIds(emptyExploreConnectionsSongIds);
      setExploreConnectionsBlockId(null);
      setTakeItFurtherContent(emptyTakeItFurtherContent);

      // Store in the same format as hasUnsavedChanges() comparison
      const emptyContent = {
        lookDeeper: lookDeeperToModalBlocks(emptyLookDeeperContent),
        oneKeyInsight: oneKeyInsightToModalBlocks(emptyOneKeyInsightContent),
        exploreConnections: emptyExploreConnectionsSongIds,
        takeItFurther: takeItFurtherToModalBlocks(emptyTakeItFurtherContent),
      };
      setOriginalContent(JSON.stringify(emptyContent));

      // Mark initial load as complete
      setTimeout(() => setInitialLoadComplete(true), 100);
    }

    async function loadModalContent() {
      if (!songId) return;

      setLoading(true);
      try {
        // Observer: load from legacy modal_blocks system
        if (selectedLevel.toLowerCase() === 'observer') {
          const allContent = await getAllModalContentForSong(songId, selectedLevel);

          // Convert modal blocks to structured content
          setLookDeeperContent(modalBlocksToLookDeeper(allContent.look_deeper));
          setOneKeyInsightContent(modalBlocksToOneKeyInsight(allContent.one_key_insight));
          setTakeItFurtherContent(modalBlocksToTakeItFurther(allContent.take_it_further));

          // Extract song IDs from explore_connections
          const exploreBlock = allContent.explore_connections.find(b => b.block_type === 'related_songs');
          const songIds = exploreBlock?.metadata?.song_ids || [];
          setExploreConnectionsSongIds(songIds);
          setExploreConnectionsBlockId(exploreBlock?.id || null);

          // Store original for dirty checking - must match the format used in hasUnsavedChanges()
          const originalLookDeeperContent = modalBlocksToLookDeeper(allContent.look_deeper);
          const originalOneKeyInsightContent = modalBlocksToOneKeyInsight(allContent.one_key_insight);
          const originalExploreConnectionsSongIds = songIds;
          const originalTakeItFurtherContent = modalBlocksToTakeItFurther(allContent.take_it_further);

          setOriginalContent(
            JSON.stringify({
              lookDeeper: lookDeeperToModalBlocks(originalLookDeeperContent),
              oneKeyInsight: oneKeyInsightToModalBlocks(originalOneKeyInsightContent),
              exploreConnections: originalExploreConnectionsSongIds,
              takeItFurther: takeItFurtherToModalBlocks(originalTakeItFurtherContent),
            })
          );
        } else {
          // Other levels: load from production tables (song_modal_blocks, song_pathway_cards)
          // Map selectedLevel to slug format for API calls
          const levelSlugMap: Record<string, string> = {
            'explorer': 'explorer',
            'sense-maker': 'senseMaker',
            'advocate': 'advocate',
            'catalyst': 'catalyst',
            'change-maker': 'changeMaker',
          };

          const levelSlug = levelSlugMap[selectedLevel];
          if (!levelSlug) {
            resetToEmpty();
            return;
          }

          // Fetch all blocks for this song + level
          const [lookDeeperBlocks, oneKeyInsightBlocks, exploreConnectionsBlocks] = await Promise.all([
            fetchModalBlocks({ songId, civicLevelSlug: levelSlug, modalKey: 'look_deeper', includeDrafts: true }),
            fetchModalBlocks({ songId, civicLevelSlug: levelSlug, modalKey: 'one_key_insight', includeDrafts: true }),
            fetchModalBlocks({ songId, civicLevelSlug: levelSlug, modalKey: 'explore_connections', includeDrafts: true }),
          ]);

          // Fetch pathway cards for Take It Further
          const pathwayCards = await fetchPathwayCards({ songId, civicLevelSlug: levelSlug, includeDrafts: true });

          // Check if content exists
          const hasContent =
            lookDeeperBlocks.length > 0 ||
            oneKeyInsightBlocks.length > 0 ||
            pathwayCards.length > 0;

          // Auto-seed if no content exists
          if (!hasContent) {
            console.log('[ActionModalsEditor] No content found. Auto-seeding level:', levelSlug);
            const seedResult = await seedActionModalsForLevel({ songId, civicLevelSlug: levelSlug });

            if (seedResult.success) {
              console.log('[ActionModalsEditor] Seeded successfully. Reloading...');
              // Reload content after seeding
              loadModalContent();
              return;
            } else {
              console.warn('[ActionModalsEditor] Seeding failed:', seedResult.error);
              resetToEmpty();
              return;
            }
          }

          // Convert production blocks to editor format
          const narrativeSections: Array<{ id: string; title: string; body: string; locked?: boolean }> = [];
          const timelineEvents: Array<{ id: string; year: string; title: string; description: string }> = [];

          // Parse Look Deeper blocks
          lookDeeperBlocks.forEach((block) => {
            if (block.block_type === 'richText' || block.block_type === 'callout') {
              narrativeSections.push({
                id: block.id,
                title: block.block_title || 'Untitled',
                body: block.block_body || '',
                locked: false,
              });
            } else if (block.block_type === 'prompt') {
              // Prompts can be shown as narrative sections in the editor
              narrativeSections.push({
                id: block.id,
                title: block.block_title || 'Reflection',
                body: block.block_prompt || block.block_body || '',
                locked: false,
              });
            }
          });

          setLookDeeperContent({ narrativeSections, timelineEvents });

          // Parse One Key Insight
          let oneKeyInsight: OneKeyInsightContent = { insight: '' };
          if (oneKeyInsightBlocks.length > 0) {
            const block = oneKeyInsightBlocks[0];

            // Check if this is insight_chips format (Explorer level)
            if (block.block_type === 'insight_chips' && block.content) {
              const content = typeof block.content === 'string'
                ? JSON.parse(block.content)
                : block.content;
              oneKeyInsight = { chips: content.chips || [] };
            } else {
              // Regular insight text
              oneKeyInsight = { insight: block.block_body || block.block_prompt || '' };
            }
          }
          setOneKeyInsightContent(oneKeyInsight);

          // Parse Explore Connections (managed separately via ExploreConnectionsPreview component)
          setExploreConnectionsSongIds([]);
          setExploreConnectionsBlockId(null);

          // Parse Take It Further (pathway cards managed by TakeItFurtherEditorEnhanced)
          setTakeItFurtherContent({
            actionType: pathwayCards.length > 0 ? 'custom' : 'collect_only',
          });

          // Store original for dirty checking - must match the format used in hasUnsavedChanges()
          const originalLookDeeperContent = { narrativeSections, timelineEvents };
          const originalOneKeyInsightContent = oneKeyInsight;
          const originalExploreConnectionsSongIds: string[] = [];
          const originalTakeItFurtherContent = {
            actionType: pathwayCards.length > 0 ? ('custom' as const) : ('collect_only' as const),
          };

          setOriginalContent(JSON.stringify({
            lookDeeper: lookDeeperToModalBlocks(originalLookDeeperContent),
            oneKeyInsight: oneKeyInsightToModalBlocks(originalOneKeyInsightContent),
            exploreConnections: originalExploreConnectionsSongIds,
            takeItFurther: takeItFurtherToModalBlocks(originalTakeItFurtherContent),
          }));
        }
      } catch (err) {
        console.error('Failed to load modal content:', err);
        resetToEmpty();
      } finally {
        setLoading(false);
        // Mark initial load as complete after a short delay to ensure state has settled
        setTimeout(() => setInitialLoadComplete(true), 100);
      }
    }

    function hasUnsavedChanges(): boolean {
      // Never report changes during loading or before initial load completes
      if (loading || !initialLoadComplete) return false;
      if (!originalContent) return false;

      // Check if lens cards have changed (for Explorer level)
      if (lensCardsChanged) return true;

      // Check if Explorer Look Deeper scenario has changed (for Explorer level)
      if (explorerLookDeeperChanged) return true;

      // Check if Explorer Timeline has changed (for Explorer level)
      if (explorerTimelineChanged) return true;

      // Check if Observer Quiz has changed (for Observer level)
      if (observerQuizChanged) return true;

      const currentContent = JSON.stringify({
        lookDeeper: lookDeeperToModalBlocks(lookDeeperContent),
        oneKeyInsight: oneKeyInsightToModalBlocks(oneKeyInsightContent),
        exploreConnections: exploreConnectionsSongIds,
        takeItFurther: takeItFurtherToModalBlocks(takeItFurtherContent),
      });

      return currentContent !== originalContent;
    }

    useEffect(() => {
      // Don't detect changes while loading or before initial load completes
      if (loading || !initialLoadComplete) return;
      onContentChange?.(hasUnsavedChanges());
    }, [lookDeeperContent, oneKeyInsightContent, exploreConnectionsSongIds, takeItFurtherContent, lensCardsChanged, explorerLookDeeperChanged, explorerTimelineChanged, observerQuizChanged, loading, initialLoadComplete]);

    async function handleSave(): Promise<boolean> {
      if (!songId) return false;

      try {
        console.log('[ActionModalsEditor] Saving modal content for level:', selectedLevel);

        // Save lens cards if there are changes (Explorer level)
        if (lensCardsChanged && lensCardsEditorRef.current) {
          const lensCardsSaved = await lensCardsEditorRef.current.save();
          if (!lensCardsSaved) {
            throw new Error('Failed to save lens cards');
          }
          setLensCardsChanged(false);
        }

        // Save Explorer Look Deeper scenario if there are changes (Explorer level only)
        if (explorerLookDeeperChanged && explorerLookDeeperEditorRef.current) {
          const explorerLookDeeperSaved = await explorerLookDeeperEditorRef.current.save();
          if (!explorerLookDeeperSaved) {
            throw new Error('Failed to save Explorer Look Deeper scenario');
          }
          setExplorerLookDeeperChanged(false);
        }

        // Save Observer Quiz if there are changes (Observer level only)
        if (observerQuizChanged && observerQuizEditorRef.current) {
          const quizSaved = await observerQuizEditorRef.current.save();
          if (!quizSaved) {
            throw new Error('Failed to save Observer quiz');
          }
          setObserverQuizChanged(false);
        }

        // Save Explorer Timeline events if there are changes (Explorer level only)
        if (explorerTimelineChanged && explorerTimelineEditorRef.current) {
          const timelineSaved = await explorerTimelineEditorRef.current.save();
          if (!timelineSaved) {
            throw new Error('Failed to save Explorer Timeline events');
          }
          setExplorerTimelineChanged(false);
        }

        // Observer level: save to legacy system (modal_blocks)
        if (selectedLevel.toLowerCase() === 'observer') {
          // Convert structured content to modal blocks
          const lookDeeperBlocks = lookDeeperToModalBlocks(lookDeeperContent);
          const oneKeyInsightBlocks = oneKeyInsightToModalBlocks(oneKeyInsightContent);

          // Create explore_connections block
          const exploreConnectionsBlocks = exploreConnectionsSongIds.length > 0 ? [{
            id: exploreConnectionsBlockId || 'related-songs',
            block_type: 'related_songs' as const,
            title: null,
            body: null,
            url: null,
            xp_value: 0,
            metadata: { song_ids: exploreConnectionsSongIds },
            enabled: true,
          }] : [];

          // Save to legacy modal_blocks system
          const results = await Promise.all([
            saveModalContent(songId, selectedLevel, 'look_deeper', lookDeeperBlocks),
            saveModalContent(songId, selectedLevel, 'one_key_insight', oneKeyInsightBlocks),
            saveModalContent(songId, selectedLevel, 'explore_connections', exploreConnectionsBlocks),
            saveModalContent(songId, selectedLevel, 'take_it_further', []),
          ]);

          console.log('[ActionModalsEditor] Legacy save results:', results);

          // Update original content - must match the format used in hasUnsavedChanges()
          setOriginalContent(
            JSON.stringify({
              lookDeeper: lookDeeperToModalBlocks(lookDeeperContent),
              oneKeyInsight: oneKeyInsightToModalBlocks(oneKeyInsightContent),
              exploreConnections: exploreConnectionsSongIds,
              takeItFurther: takeItFurtherToModalBlocks(takeItFurtherContent),
            })
          );
        } else {
          // Other levels: save to production tables (song_modal_blocks, song_pathway_cards)
          // Map selectedLevel to slug format
          const levelSlugMap: Record<string, string> = {
            'explorer': 'explorer',
            'sense-maker': 'senseMaker',
            'advocate': 'advocate',
            'catalyst': 'catalyst',
            'change-maker': 'changeMaker',
          };

          const levelSlug = levelSlugMap[selectedLevel];
          if (!levelSlug) {
            throw new Error(`Unknown level: ${selectedLevel}`);
          }

          // Fetch existing blocks to get their IDs and civic_level_id
          const [existingLookDeeperBlocks, existingOneKeyInsightBlocks] = await Promise.all([
            fetchModalBlocks({ songId, civicLevelSlug: levelSlug, modalKey: 'look_deeper', includeDrafts: true }),
            fetchModalBlocks({ songId, civicLevelSlug: levelSlug, modalKey: 'one_key_insight', includeDrafts: true }),
          ]);

          // Get civic_level_id from existing blocks or fetch it
          let civicLevelId: string | undefined;
          if (existingLookDeeperBlocks.length > 0) {
            civicLevelId = existingLookDeeperBlocks[0].civic_level_id;
          } else if (existingOneKeyInsightBlocks.length > 0) {
            civicLevelId = existingOneKeyInsightBlocks[0].civic_level_id;
          }

          // If no existing blocks, we need to fetch the civic_level_id
          if (!civicLevelId) {
            // This shouldn't happen because auto-seeding creates blocks first
            console.warn('[ActionModalsEditor] No civic_level_id found. Skipping save.');
            return false;
          }

          // Update Look Deeper blocks
          const lookDeeperUpdates = lookDeeperContent.narrativeSections.map((section, index) => {
            const existingBlock = existingLookDeeperBlocks.find(b => b.id === section.id);
            return saveModalBlock({
              id: existingBlock?.id,
              song_id: songId,
              civic_level_id: civicLevelId!,
              modal_key: 'look_deeper',
              block_type: 'richText',
              sort_order: index,
              status: 'published',
              enabled: true,
              block_title: section.title,
              block_body: section.body,
            });
          });

          // Update One Key Insight block
          const oneKeyInsightUpdates = [];

          // Check if this is Explorer level with chips
          const isExplorerChips = selectedLevel.toLowerCase() === 'explorer' && oneKeyInsightContent.chips;

          if (isExplorerChips && oneKeyInsightContent.chips && oneKeyInsightContent.chips.length > 0) {
            // Save Explorer chips to content jsonb
            const existingBlock = existingOneKeyInsightBlocks[0];
            oneKeyInsightUpdates.push(
              saveModalBlock({
                id: existingBlock?.id,
                song_id: songId,
                civic_level_id: civicLevelId,
                modal_key: 'one_key_insight',
                block_type: 'insight_chips',
                sort_order: 1,
                status: 'published',
                enabled: true,
                content: { chips: oneKeyInsightContent.chips },
              })
            );
          } else if (oneKeyInsightContent.insight) {
            // Save regular insight text
            const existingBlock = existingOneKeyInsightBlocks[0];
            oneKeyInsightUpdates.push(
              saveModalBlock({
                id: existingBlock?.id,
                song_id: songId,
                civic_level_id: civicLevelId,
                modal_key: 'one_key_insight',
                block_type: 'richText',
                sort_order: 0,
                status: 'published',
                enabled: true,
                block_body: oneKeyInsightContent.insight,
              })
            );
          }

          // Execute all updates
          const results = await Promise.all([...lookDeeperUpdates, ...oneKeyInsightUpdates]);
          const failed = results.filter(r => !r.success);

          if (failed.length > 0) {
            console.error('[ActionModalsEditor] Some saves failed:', failed);
            throw new Error(`Failed to save ${failed.length} blocks`);
          }

          console.log('[ActionModalsEditor] Production tables save complete');

          // Update original content - must match the format used in hasUnsavedChanges()
          setOriginalContent(
            JSON.stringify({
              lookDeeper: lookDeeperToModalBlocks(lookDeeperContent),
              oneKeyInsight: oneKeyInsightToModalBlocks(oneKeyInsightContent),
              exploreConnections: exploreConnectionsSongIds,
              takeItFurther: takeItFurtherToModalBlocks(takeItFurtherContent),
            })
          );
        }

        onContentChange?.(false);
        return true;
      } catch (err) {
        console.error('Failed to save modal content:', err);
        return false;
      }
    }

    // Expose save function to parent
    useImperativeHandle(ref, () => ({
      save: handleSave,
      hasUnsavedChanges,
    }));

    const isDisabled = !songId;

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-[#808080] text-[14px]">Loading modal content...</div>
        </div>
      );
    }

    const toggleSection = (section: ModalTab) => {
      if (expandedSection === section) {
        setExpandedSection(null);
      } else {
        setExpandedSection(section);
      }
    };

    return (
      <div className="space-y-6">
        {isDisabled && (
          <div className="bg-[#FFB86C]/10 border border-[#FFB86C]/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#FFB86C] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[14px] font-semibold text-[#FFB86C] mb-1">Save Draft First</p>
              <p className="text-[13px] text-[#A0A0A0]">
                Create the song record by clicking "Save Draft" above, then return to this tab to
                configure Action Modals.
              </p>
            </div>
          </div>
        )}

        <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-6">
          <div className="mb-6">
            <h2 className="text-[18px] font-semibold text-[#F5F5F5] mb-1">Action Modals</h2>
            <p className="text-[12px] text-[#808080]">
              Configure modal content for each civic level
            </p>
          </div>

          {/* Civic Level Selector - Desktop: Horizontal Chips */}
          <div className="mb-6 hidden md:block">
            <label className="block text-[13px] font-medium text-[#A0A0A0] mb-3">
              Select Civic Level
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {CIVIC_LEVELS.map((level) => (
                <button
                  key={level.key}
                  onClick={() => !isDisabled && setSelectedLevel(level.key)}
                  disabled={isDisabled}
                  className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    selectedLevel === level.key
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border border-[#40E0D0]/30'
                      : 'bg-[#0A0A0A] text-[#A0A0A0] border border-[#282828] hover:border-[#404040]'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          {/* Civic Level Selector - Mobile: Vertical List */}
          <div className="mb-8 md:hidden">
            <label className="block text-[13px] font-medium text-[#A0A0A0] mb-3">
              Select Civic Level
            </label>
            <div className="space-y-2">
              {CIVIC_LEVELS.map((level) => (
                <button
                  key={level.key}
                  onClick={() => !isDisabled && setSelectedLevel(level.key)}
                  disabled={isDisabled}
                  className={`w-full px-5 py-4 rounded-xl text-[15px] font-medium transition-all text-left ${
                    selectedLevel === level.key
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border-2 border-[#40E0D0]'
                      : 'bg-[#0A0A0A] text-[#A0A0A0] border border-[#282828]'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop: Modal Tabs */}
          <div className="mb-6 hidden md:block">
            <div className="flex items-center gap-2 border-b border-[#282828]">
              {MODAL_TABS.map((modal) => (
                <button
                  key={modal.key}
                  onClick={() => !isDisabled && setActiveModal(modal.key)}
                  disabled={isDisabled}
                  className={`px-4 py-3 text-[13px] font-medium transition-colors border-b-2 ${
                    activeModal === modal.key
                      ? 'text-[#40E0D0] border-[#40E0D0]'
                      : 'text-[#808080] border-transparent hover:text-[#A0A0A0]'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {modal.label}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop: Modal Content Editors */}
          <div className="pb-6 hidden md:block">
            {activeModal === 'look_deeper' && (
              selectedLevel === 'observer' && songId ? (
                <ExplorerTimelineEditor
                  ref={explorerTimelineEditorRef}
                  songId={songId}
                  onContentChange={setExplorerTimelineChanged}
                  modalContext="look_deeper"
                />
              ) : selectedLevel === 'explorer' && songId ? (
                <ExplorerLookDeeperEditor
                  ref={explorerLookDeeperEditorRef}
                  songId={songId}
                  onUpdate={() => {
                    // Refresh data if needed
                  }}
                  onDirtyStateChange={setExplorerLookDeeperChanged}
                />
              ) : (
                <LookDeeperEditorEnhanced
                  content={lookDeeperContent}
                  onChange={setLookDeeperContent}
                  disabled={isDisabled}
                  civicLevel={selectedLevel}
                />
              )
            )}

            {activeModal === 'one_key_insight' && (
              selectedLevel === 'explorer' && songId ? (
                <LensCardsEditor
                  ref={lensCardsEditorRef}
                  songId={songId}
                  onChange={setLensCardsChanged}
                />
              ) : selectedLevel === 'observer' && songId ? (
                <ObserverOneKeyInsightQuizEditor
                  ref={observerQuizEditorRef}
                  songId={songId}
                  onChange={setObserverQuizChanged}
                />
              ) : (
                <OneKeyInsightEditorEnhanced
                  content={oneKeyInsightContent}
                  onChange={setOneKeyInsightContent}
                  disabled={isDisabled}
                  civicLevel={selectedLevel}
                />
              )
            )}

            {activeModal === 'explore_connections' && songId && (
              selectedLevel === 'explorer' ? (
                <ExplorerTimelineEditor
                  ref={explorerTimelineEditorRef}
                  songId={songId}
                  onContentChange={setExplorerTimelineChanged}
                />
              ) : (
                <ExploreConnectionsPreview
                  songId={songId}
                  disabled={isDisabled}
                  onChange={setExploreConnectionsSongIds}
                  savedSongIds={exploreConnectionsSongIds}
                />
              )
            )}

            {activeModal === 'take_it_further' && (
              <TakeItFurtherEditorEnhanced
                content={takeItFurtherContent}
                onChange={setTakeItFurtherContent}
                disabled={isDisabled}
                civicLevel={CIVIC_LEVELS.find(l => l.key === selectedLevel)?.label || ''}
              />
            )}
          </div>

          {/* Mobile: Collapsible Sections */}
          <div className="md:hidden space-y-4">
            {MODAL_TABS.map((modal) => (
              <div key={modal.key} className="bg-[#0A0A0A] border border-[#282828] rounded-xl overflow-hidden">
                <button
                  onClick={() => !isDisabled && toggleSection(modal.key)}
                  disabled={isDisabled}
                  className="w-full px-5 py-4 flex items-center justify-between text-left transition-colors disabled:opacity-50"
                >
                  <div className="flex-1">
                    <h3 className={`text-[15px] font-semibold mb-0.5 ${
                      expandedSection === modal.key ? 'text-[#40E0D0]' : 'text-[#F5F5F5]'
                    }`}>
                      {modal.label}
                    </h3>
                    {expandedSection !== modal.key && (
                      <p className="text-[12px] text-[#808080]">
                        Tap to expand
                      </p>
                    )}
                  </div>
                  {expandedSection === modal.key ? (
                    <ChevronUp className="w-5 h-5 text-[#40E0D0] flex-shrink-0 ml-3" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#808080] flex-shrink-0 ml-3" />
                  )}
                </button>

                {expandedSection === modal.key && (
                  <div className="px-5 pb-6 pt-2 border-t border-[#282828]">
                    {modal.key === 'look_deeper' && (
                      selectedLevel === 'observer' && songId ? (
                        <ExplorerTimelineEditor
                          ref={explorerTimelineEditorRef}
                          songId={songId}
                          onContentChange={setExplorerTimelineChanged}
                          modalContext="look_deeper"
                        />
                      ) : selectedLevel === 'explorer' && songId ? (
                        <ExplorerLookDeeperEditor
                          ref={explorerLookDeeperEditorRef}
                          songId={songId}
                          onUpdate={() => {
                            // Refresh data if needed
                          }}
                          onDirtyStateChange={setExplorerLookDeeperChanged}
                        />
                      ) : (
                        <LookDeeperEditorEnhanced
                          content={lookDeeperContent}
                          onChange={setLookDeeperContent}
                          disabled={isDisabled}
                          civicLevel={selectedLevel}
                        />
                      )
                    )}

                    {modal.key === 'one_key_insight' && (
                      selectedLevel === 'explorer' && songId ? (
                        <LensCardsEditor
                          ref={lensCardsEditorRef}
                          songId={songId}
                          onChange={setLensCardsChanged}
                        />
                      ) : selectedLevel === 'observer' && songId ? (
                        <ObserverOneKeyInsightQuizEditor
                          ref={observerQuizEditorRef}
                          songId={songId}
                          onChange={setObserverQuizChanged}
                        />
                      ) : (
                        <OneKeyInsightEditorEnhanced
                          content={oneKeyInsightContent}
                          onChange={setOneKeyInsightContent}
                          disabled={isDisabled}
                          civicLevel={selectedLevel}
                        />
                      )
                    )}

                    {modal.key === 'explore_connections' && songId && (
                      selectedLevel === 'explorer' ? (
                        <ExplorerTimelineEditor
                          ref={explorerTimelineEditorRef}
                          songId={songId}
                          onContentChange={setExplorerTimelineChanged}
                        />
                      ) : (
                        <ExploreConnectionsPreview
                          songId={songId}
                          disabled={isDisabled}
                          onChange={setExploreConnectionsSongIds}
                          savedSongIds={exploreConnectionsSongIds}
                        />
                      )
                    )}

                    {modal.key === 'take_it_further' && (
                      <TakeItFurtherEditorEnhanced
                        content={takeItFurtherContent}
                        onChange={setTakeItFurtherContent}
                        disabled={isDisabled}
                        civicLevel={CIVIC_LEVELS.find(l => l.key === selectedLevel)?.label || ''}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

ActionModalsEditorNew.displayName = 'ActionModalsEditorNew';

export default ActionModalsEditorNew;

// ============================================================================
// OBSERVER ONE KEY INSIGHT QUIZ EDITOR
// ============================================================================

interface ObserverOneKeyInsightQuizEditorProps {
  songId: string;
  onChange?: (hasChanges: boolean) => void;
}

export interface ObserverOneKeyInsightQuizEditorHandle {
  save: () => Promise<boolean>;
}

const ObserverOneKeyInsightQuizEditor = forwardRef<ObserverOneKeyInsightQuizEditorHandle, ObserverOneKeyInsightQuizEditorProps>(
  ({ songId, onChange }, ref) => {
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState({
    question: '',
    option_a: '',
    option_b: '',
    option_c: '',
    correct_answer: 'a' as 'a' | 'b' | 'c',
    explanation: '',
  });
  const [originalData, setOriginalData] = useState('');

  useEffect(() => {
    loadQuizData();
  }, [songId]);

  useEffect(() => {
    // Notify parent of changes
    if (loading || !originalData) return;
    const currentData = JSON.stringify(quizData);
    onChange?.(currentData !== originalData);
  }, [quizData, originalData, loading]);

  async function loadQuizData() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('one_key_insight_question, one_key_insight_option_a, one_key_insight_option_b, one_key_insight_option_c, one_key_insight_correct_answer, one_key_insight_explanation')
        .eq('id', songId)
        .single();

      if (error) throw error;

      if (data) {
        const loadedData = {
          question: data.one_key_insight_question || '',
          option_a: data.one_key_insight_option_a || '',
          option_b: data.one_key_insight_option_b || '',
          option_c: data.one_key_insight_option_c || '',
          correct_answer: (data.one_key_insight_correct_answer || 'a') as 'a' | 'b' | 'c',
          explanation: data.one_key_insight_explanation || '',
        };
        setQuizData(loadedData);
        setOriginalData(JSON.stringify(loadedData));
      }
    } catch (err) {
      console.error('[ObserverOneKeyInsightQuizEditor] Error loading quiz data:', err);
    } finally {
      setLoading(false);
    }
  }

  useImperativeHandle(ref, () => ({
    save: async () => {
      try {
        const { error } = await supabase
          .from('songs')
          .update({
            one_key_insight_question: quizData.question,
            one_key_insight_option_a: quizData.option_a,
            one_key_insight_option_b: quizData.option_b,
            one_key_insight_option_c: quizData.option_c,
            one_key_insight_correct_answer: quizData.correct_answer,
            one_key_insight_explanation: quizData.explanation,
          })
          .eq('id', songId);

        if (error) throw error;

        // Update original data after successful save
        setOriginalData(JSON.stringify(quizData));
        return true;
      } catch (err) {
        console.error('[ObserverOneKeyInsightQuizEditor] Error saving quiz data:', err);
        return false;
      }
    },
  }));

  function updateField(field: keyof typeof quizData, value: string) {
    setQuizData(prev => ({ ...prev, [field]: value }));
  }

  if (loading) {
    return (
      <div className="bg-[#1A1A1A] border border-[#282828] rounded-lg p-8 text-center">
        <p className="text-[14px] text-[#A0A0A0]">Loading...</p>
      </div>
    );
  }

  const charCount = quizData.explanation.length;
  const recommendedMax = 400;

  return (
    <div className="space-y-8">
      <div>
        <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4 mb-3">
          <div className="mb-3">
            <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
              Question
            </label>
          </div>
          <textarea
            value={quizData.question}
            onChange={(e) => updateField('question', e.target.value)}
            rows={3}
            placeholder="e.g., What percentage of people killed by U.S. police were Black Americans in 2020?"
            className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
          />
        </div>

        <div className="space-y-3">
          <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4">
            <div className="mb-3">
              <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
                Option A
              </label>
            </div>
            <input
              type="text"
              value={quizData.option_a}
              onChange={(e) => updateField('option_a', e.target.value)}
              placeholder="First answer option"
              className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
            />
          </div>

          <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4">
            <div className="mb-3">
              <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
                Option B
              </label>
            </div>
            <input
              type="text"
              value={quizData.option_b}
              onChange={(e) => updateField('option_b', e.target.value)}
              placeholder="Second answer option"
              className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
            />
          </div>

          <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4">
            <div className="mb-3">
              <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
                Option C
              </label>
            </div>
            <input
              type="text"
              value={quizData.option_c}
              onChange={(e) => updateField('option_c', e.target.value)}
              placeholder="Third answer option"
              className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
            />
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4 mt-3">
          <div className="mb-3">
            <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
              Correct Answer
            </label>
          </div>
          <select
            value={quizData.correct_answer}
            onChange={(e) => updateField('correct_answer', e.target.value)}
            className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
          >
            <option value="a">Option A</option>
            <option value="b">Option B</option>
            <option value="c">Option C</option>
          </select>
        </div>

        <div className="bg-[#0A0A0A] border border-[#282828] rounded-lg p-4 mt-3">
          <div className="mb-3">
            <label className="block text-[11px] font-medium text-[#808080] mb-1.5">
              Explanation
            </label>
          </div>
          <textarea
            value={quizData.explanation}
            onChange={(e) => updateField('explanation', e.target.value)}
            rows={4}
            placeholder="Provide the full context and explanation shown after correct answer..."
            className="w-full px-3 py-2 bg-[#181818] border border-[#282828] rounded text-[13px] text-[#F5F5F5] placeholder:text-[#606060] leading-relaxed resize-vertical focus:border-[#40E0D0]/30 focus:outline-none transition-colors"
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-[#606060]">
              Recommended: 240-400 characters
            </p>
            <p className={`text-[11px] font-medium ${charCount > recommendedMax ? 'text-[#FF9F43]' : 'text-[#666666]'}`}>
              {charCount} / {recommendedMax}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

ObserverOneKeyInsightQuizEditor.displayName = 'ObserverOneKeyInsightQuizEditor';

export { ObserverOneKeyInsightQuizEditor };
